
import { supabase } from './supabaseClient';
import { NotificationType, NotificationPriority, CreateNotificationInput } from '../types';

import { devError } from '@/utils/devLog';

/** Évite les doublons (ex. trigger SQL notify_on_order_event + notifyUser après update statut). */
function isDuplicateCommandeStatutNotification(
    existing: Record<string, any> | null | undefined,
    incoming: Record<string, any> | undefined
): boolean {
    if (incoming?.order_id == null) return false;
    if (!existing || existing.order_id == null) return false;
    if (String(existing.order_id) !== String(incoming.order_id)) return false;
    const s0 = existing.order_status ?? existing.new_status ?? existing.statut;
    const s1 = incoming.order_status ?? incoming.new_status ?? incoming.statut;
    return String(s0 ?? '') === String(s1 ?? '');
}

export const notifyUser = async (
    userId: string, 
    message: string, 
    type: NotificationType, 
    lien: string,
    priority: NotificationPriority = NotificationPriority.NORMAL,
    metadata?: Record<string, any>
) => {
    try {
        if (type === NotificationType.COMMANDE_STATUT && metadata?.order_id != null) {
            const since = new Date(Date.now() - 10_000).toISOString();
            const { data: recent, error: recentErr } = await supabase
                .from('notifications')
                .select('metadata')
                .eq('user_id', userId)
                .eq('type', NotificationType.COMMANDE_STATUT)
                .gte('created_at', since)
                .limit(30);
            if (recentErr) {
                devError('notifyUser dédoublonnage (lecture):', recentErr);
            } else if (recent?.some((row: { metadata?: Record<string, any> }) => isDuplicateCommandeStatutNotification(row.metadata, metadata))) {
                return;
            }
        }

        const { error } = await supabase.rpc('create_notification', {
            p_user_id: userId,
            p_type: type,
            p_message: message,
            p_lien: lien,
            p_priority: priority,
            p_metadata: metadata || {},
            p_expires_at: null,
            p_channels: ['in_app']
        });

        if (error) {
            devError("Erreur create_notification:", error);
            // Fallback vers l'ancienne méthode si la fonction RPC n'existe pas encore
            await supabase.from('notifications').insert({
                user_id: userId,
                message,
                type,
                lien,
                priority,
                metadata: metadata || {},
                date: new Date().toISOString(),
                created_at: new Date().toISOString()
            });
        }
    } catch (e) {
        devError("Erreur notifyUser:", e);
    }
};

export const notifyAdmins = async (
    message: string, 
    type: NotificationType, 
    lien: string,
    priority: NotificationPriority = NotificationPriority.NORMAL,
    metadata?: Record<string, any>
) => {
    try {
        // Appeler la fonction avec les types en string (la fonction SQL les cast automatiquement)
        const { error } = await supabase.rpc('notify_all_admins', {
            p_type: type as string,  // Passer comme string, la fonction SQL cast
            p_message: message,
            p_lien: lien,
            p_priority: priority as string,  // Passer comme string, la fonction SQL cast
            p_metadata: metadata || {}
        });

        if (error) {
            devError("RPC notify_all_admins failed:", error);
            // Fallback : créer les notifications directement
            const { data: admins } = await supabase.from('profiles').select('id').eq('role', 'admin');
            if (admins && admins.length > 0) {
                // Utiliser create_notification si disponible, sinon insert direct
                for (const admin of admins) {
                    try {
                        await supabase.rpc('create_notification', {
                            p_user_id: admin.id,
                            p_type: type,
                            p_message: message,
                            p_lien: lien,
                            p_priority: priority,
                            p_metadata: metadata || {}
                        });
                    } catch {
                        // Si create_notification n'existe pas, utiliser insert direct
                        await supabase.from('notifications').insert({
                            user_id: admin.id,
                            message,
                            type,
                            lien,
                            priority,
                            metadata: metadata || {},
                            date: new Date().toISOString(),
                            created_at: new Date().toISOString()
                        });
                    }
                }
            }
        }
    } catch (e) {
        devError("Erreur notifyAdmins:", e);
    }
};

export const createNotification = async (input: CreateNotificationInput): Promise<string | null> => {
    try {
        const { data, error } = await supabase.rpc('create_notification', {
            p_user_id: input.user_id,
            p_type: input.type,
            p_message: input.message,
            p_lien: input.lien,
            p_priority: input.priority || NotificationPriority.NORMAL,
            p_metadata: input.metadata || {},
            p_expires_at: input.expires_at || null,
            p_channels: input.channels || ['in_app']
        });

        if (error) {
            devError("Erreur createNotification:", error);
            return null;
        }

        return data;
    } catch (e) {
        devError("Erreur createNotification:", e);
        return null;
    }
};

export const notificationService = {
    notifyUser,
    notifyAdmins,
    createNotification
};
